<script setup lang="ts">
import { computed } from 'vue';
import { UiIconButton, UiInputField, UiSelect, UiStandardButton, UiText, UiTooltip } from '@shopexperts/shared';
import type { AdminPage, PageSchema } from '@shopexperts/shared';

const props = defineProps<{
  schema: PageSchema;
  schemas: PageSchema[];
  isLoadingSchemas: boolean;
  pages: AdminPage[];
  selectedPageId?: number | null;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}>();

const search = defineModel<string>('search', { required: true });

const emit = defineEmits<{
  newPage: [];
  selectPage: [page: AdminPage];
  selectSchema: [schemaId: number];
  editSchema: [];
  loadMore: [];
}>();

/**
 * The current schema is always an option even before the schema list resolves,
 * so the switcher never renders as empty while it loads.
 */
const schemaOptions = computed(() => {
  const options = props.schemas.map(schema => ({ label: schema.label, value: String(schema.id) }));
  if (!options.some(option => option.value === String(props.schema.id))) {
    options.unshift({ label: props.schema.label, value: String(props.schema.id) });
  }
  return options;
});

const selectedSchemaValue = computed(() => String(props.schema.id));

function onSchemaChange(value: string | string[]) {
  const next = Array.isArray(value) ? value[0] : value;
  const schemaId = next ? parseInt(next, 10) : NaN;
  if (!Number.isFinite(schemaId) || schemaId === props.schema.id) return;
  emit('selectSchema', schemaId);
}

function pageTitle(page: AdminPage): string {
  const title = page.metafields?.title;
  return typeof title === 'string' && title.trim() ? title.trim() : page.path;
}
</script>

<template>
  <aside class="flex min-h-0 w-72 shrink-0 flex-col overflow-hidden border-r border-se-gray-300 bg-white">
    <div class="border-b border-se-gray-300 px-4 py-3">
      <UiText as="h2" variant="h6" text="Pages" />
      <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-700" text="Select a page to edit." />
    </div>

    <div class="flex flex-col gap-3 border-b border-se-gray-300 p-4">
      <div class="flex flex-col gap-1">
        <UiText as="label" variant="caption-normal" class="text-se-gray-700" text="Schema" />
        <div class="flex items-center gap-1">
          <div class="min-w-0 flex-1">
            <UiSelect
              :model-value="selectedSchemaValue"
              :options="schemaOptions"
              :disabled="props.isLoadingSchemas"
              data-testid="schema-switcher"
              @update:model-value="onSchemaChange"
            />
          </div>
          <UiTooltip content="Edit schema fields">
            <UiIconButton
              icon="pencil"
              aria-label="Edit schema fields"
              variant="ghost"
              class="h-9 w-9 shrink-0"
              data-testid="edit-schema"
              @click="emit('editSchema')"
            />
          </UiTooltip>
        </div>
      </div>
      <UiInputField v-model="search" placeholder="Search pages..." />
      <UiStandardButton label="New page" icon="plus" variant="outline" size="sm" @click="emit('newPage')" />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto p-3">
      <UiText v-if="props.isLoading" as="p" variant="p-small" text="Loading pages..." class="p-3 text-se-gray-700" />
      <div v-else class="flex flex-col gap-1">
        <button
          v-for="page in props.pages"
          :key="page.id"
          type="button"
          class="rounded-md px-3 py-2.5 text-left hover:bg-se-gray-100"
          :class="{ 'bg-se-gray-100 ring-1 ring-inset ring-se-gray-300': props.selectedPageId === page.id }"
          @click="emit('selectPage', page)"
        >
          <UiText as="span" variant="p-small" class="block truncate text-se-dark" :text="pageTitle(page)" />
          <span class="flex items-center gap-1.5">
            <span
              class="h-1.5 w-1.5 shrink-0 rounded-full"
              :class="page.status === 'published' ? 'bg-emerald-500' : 'bg-amber-500'"
              :title="page.status === 'published' ? 'Published' : 'Draft'"
            />
            <UiText as="span" variant="caption-normal" class="block truncate text-se-gray-700" :text="page.path" />
          </span>
        </button>
        <UiText
          v-if="!props.pages.length"
          as="p"
          variant="p-small"
          class="p-3 text-se-gray-700"
          text="No pages in this schema yet."
        />
        <UiStandardButton
          v-if="props.hasNextPage"
          :label="props.isFetchingNextPage ? 'Loading...' : 'Load more'"
          variant="ghost"
          size="sm"
          :disabled="props.isFetchingNextPage"
          @click="emit('loadMore')"
        />
      </div>
    </div>
  </aside>
</template>
